// Panel de la página Configuración. Extraído de pages/Configuracion.tsx
// (modularización 2026-06-11) sin cambios de comportamiento.
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, api, type Marca, type Modelo } from "@/lib/api";
import AvisoModelosSimilares from "@/components/AvisoModelosSimilares";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";

// ============================================================================
// MODELOS
// ============================================================================

export default function ModelosPanel() {
  const toast = useToast();
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [loading, setLoading] = useState(true);
  const [marcaId, setMarcaId] = useState<number | "">("");
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [creando, setCreando] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editNombre, setEditNombre] = useState("");
  const [confirmDel, setConfirmDel] = useState<Modelo | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const [ms, mds] = await Promise.all([
        api.get<Marca[]>("/api/catalogos/marcas?incluir_inactivas=true"),
        api.get<Modelo[]>("/api/catalogos/modelos?incluir_inactivos=true"),
      ]);
      setMarcas(ms);
      setModelos(mds);
      // Auto-select primera marca activa si no hay ninguna seleccionada
      if (marcaId === "" && ms.length > 0) {
        const primera = ms.find((m) => m.activo) ?? ms[0];
        setMarcaId(primera.id);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lista = marcaId === "" ? modelos : modelos.filter((m) => m.marca_id === marcaId);

  async function crear(e: FormEvent) {
    e.preventDefault();
    if (!nombreNuevo.trim() || marcaId === "") return;
    setCreando(true);
    try {
      const m = await api.post<Modelo>("/api/catalogos/modelos", {
        marca_id: marcaId,
        nombre: nombreNuevo.trim(),
      });
      toast.success("Modelo creado", `${m.marca_nombre} / ${m.nombre}`);
      setNombreNuevo("");
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear", msg);
    } finally {
      setCreando(false);
    }
  }

  async function toggleActivo(m: Modelo) {
    try {
      await api.patch<Modelo>(`/api/catalogos/modelos/${m.id}`, { activo: !m.activo });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  function startEdit(m: Modelo) {
    setEditandoId(m.id);
    setEditNombre(m.nombre);
  }

  async function saveEdit(m: Modelo) {
    if (!editNombre.trim() || editNombre === m.nombre) {
      setEditandoId(null);
      return;
    }
    try {
      await api.patch<Modelo>(`/api/catalogos/modelos/${m.id}`, { nombre: editNombre.trim() });
      toast.success("Modelo renombrado");
      setEditandoId(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo renombrar", msg);
    }
  }

  async function eliminar(m: Modelo) {
    try {
      await api.del(`/api/catalogos/modelos/${m.id}`);
      toast.success("Modelo eliminado", m.nombre);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  if (marcas.length === 0 && !loading) {
    return (
      <div className="card text-sm text-zinc-400">
        Crea al menos una marca primero en la pestaña Marcas.
      </div>
    );
  }

  return (
    <>
      <form onSubmit={crear} className="card flex flex-wrap items-end gap-3">
        <div className="w-full sm:min-w-[200px]">
          <label className="text-xs uppercase tracking-wide text-zinc-400">Marca</label>
          <select
            className="input mt-1"
            value={marcaId}
            onChange={(e) => setMarcaId(e.target.value ? parseInt(e.target.value) : "")}
          >
            <option value="">— elige marca —</option>
            {marcas.map((m) => (
              <option key={m.id} value={m.id} disabled={!m.activo}>
                {m.nombre}{!m.activo ? " (inactiva)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:flex-1 sm:min-w-[200px]">
          <label className="text-xs uppercase tracking-wide text-zinc-400">Nuevo modelo</label>
          <input
            type="text"
            className="input mt-1"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="ej. Trace5 LTE LA, GV58LAU"
          />
        </div>
        <button
          type="submit"
          disabled={!nombreNuevo.trim() || marcaId === "" || creando}
          className="btn-primary disabled:opacity-50 w-full sm:w-auto"
        >
          {creando ? "Creando..." : "+ Agregar"}
        </button>
        <AvisoModelosSimilares nombre={nombreNuevo} className="w-full" />
      </form>

      <div className="card">
        {loading ? (
          <SkeletonRows rows={4} cols={3} />
        ) : lista.length === 0 ? (
          <p className="text-sm text-zinc-500">
            {marcaId === "" ? "Elige una marca arriba." : "Esta marca no tiene modelos todavía."}
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {lista.map((m) => (
              <li key={m.id} className={`py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${m.activo ? "" : "opacity-50"}`}>
                {editandoId === m.id ? (
                  <input
                    type="text"
                    autoFocus
                    className="input flex-1"
                    value={editNombre}
                    onChange={(e) => setEditNombre(e.target.value)}
                    onBlur={() => saveEdit(m)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(m);
                      if (e.key === "Escape") setEditandoId(null);
                    }}
                  />
                ) : (
                  <span className="text-zinc-200 flex-1">
                    <span className="text-zinc-500">{m.marca_nombre} /</span> {m.nombre}
                    {m.creado_por_nombre && (
                      <span className="block text-[11px] text-zinc-500">
                        agregado por {m.creado_por_nombre}
                        {m.creado_el
                          ? ` el ${new Date(m.creado_el).toLocaleDateString()}`
                          : ""}
                      </span>
                    )}
                  </span>
                )}
                <div className="flex flex-wrap gap-1 sm:flex-shrink-0">
                  <button onClick={() => toggleActivo(m)} className="btn-ghost text-xs">
                    {m.activo ? "Desactivar" : "Activar"}
                  </button>
                  <button onClick={() => startEdit(m)} className="btn-ghost text-xs">Renombrar</button>
                  <button
                    onClick={() => setConfirmDel(m)}
                    className="btn-ghost text-xs text-red-400 hover:text-red-300"
                  >
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={`Eliminar "${confirmDel.nombre}"`}
          description={`Modelo de ${confirmDel.marca_nombre}. Los casos viejos no se modifican.`}
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminar(confirmDel)}
        />
      )}
    </>
  );
}

